import { createSlice } from '@reduxjs/toolkit';
// Phase 6 D-17 + D-18: RTK slice for the admin FAQ tree. Mirrors the structure
// of adminSlices/rallies (createSlice with reducers + selectors blocks; named
// action and selector exports; default reducer export).
//
// Granular mutators (upsertCategory / removeCategory / upsertItem /
// removeItem / setCategorySorting / setItemSorting) let 06-03 and 06-04 react
// to a successful API call by patching the loaded tree in place — no full
// refetch on every save (D-18: refetch only on demand, never poll).
const initialState = {
    categories: null,
    loadStatus: 'idle',
    categorySortingDirty: false,
    itemSortingDirtyCategoryIds: [],
};
const adminFaqSlice = createSlice({
    name: 'adminFaq',
    initialState,
    reducers: {
        setAdminFaqCategories(state, action) {
            state.categories = action.payload;
        },
        setLoadStatus(state, action) {
            state.loadStatus = action.payload;
        },
        // upsertCategory: replace by uid OR append at the end (new categories go to
        // the bottom per UI-SPEC § "Add at bottom"). Preserves current sort order.
        upsertCategory(state, action) {
            const incoming = action.payload;
            if (!state.categories) {
                state.categories = [incoming];
                return;
            }
            const idx = state.categories.findIndex((c) => c.uid === incoming.uid);
            if (idx === -1) {
                state.categories.push(incoming);
            }
            else {
                state.categories[idx] = incoming;
            }
        },
        // removeCategory: filter by uid. Phase 5 returns 409 instead of cascading,
        // so the modal in 06-03 must confirm "category is empty" before this
        // action ever fires — no item-cascade handling here.
        removeCategory(state, action) {
            if (!state.categories)
                return;
            state.categories = state.categories.filter((c) => c.uid !== action.payload);
        },
        // upsertItem: locate the parent category by uid, then replace by item uid
        // or append at the end of that category's items array. If `previousCategoryUid`
        // is supplied (PATCH that reassigned the item to a new parent), the item is
        // ALSO removed from the previous category before being inserted into the
        // new one — single transactional move (D-19).
        upsertItem(state, action) {
            if (!state.categories)
                return;
            const { categoryUid, item, previousCategoryUid } = action.payload;
            // Remove from previous category if this is a parent-change PATCH.
            if (previousCategoryUid !== undefined && previousCategoryUid !== categoryUid) {
                const prev = state.categories.find((c) => c.uid === previousCategoryUid);
                if (prev) {
                    prev.items = prev.items.filter((i) => i.uid !== item.uid);
                }
            }
            const target = state.categories.find((c) => c.uid === categoryUid);
            if (!target)
                return;
            const idx = target.items.findIndex((i) => i.uid === item.uid);
            if (idx === -1) {
                target.items.push(item);
            }
            else {
                target.items[idx] = item;
            }
        },
        removeItem(state, action) {
            if (!state.categories)
                return;
            const { categoryUid, itemUid } = action.payload;
            const target = state.categories.find((c) => c.uid === categoryUid);
            if (!target)
                return;
            target.items = target.items.filter((i) => i.uid !== itemUid);
        },
        // setCategorySorting: payload = array of category uids in new client-side
        // order. Reorders state.categories to match. Used both during ↑↓ swaps
        // (so the list re-renders) and after the save-order POST succeeds (to
        // confirm the order is now persisted).
        setCategorySorting(state, action) {
            if (!state.categories)
                return;
            const order = action.payload;
            const byUid = new Map(state.categories.map((c) => [c.uid, c]));
            const reordered = [];
            for (const uid of order) {
                const c = byUid.get(uid);
                if (c)
                    reordered.push(c);
            }
            // Preserve any categories not mentioned in the order array at the end
            // (defensive — should not happen in normal flow, but avoids silent drop
            // if the caller passes a partial list).
            for (const c of state.categories) {
                if (!order.includes(c.uid))
                    reordered.push(c);
            }
            state.categories = reordered;
        },
        setItemSorting(state, action) {
            if (!state.categories)
                return;
            const { categoryUid, itemUids } = action.payload;
            const target = state.categories.find((c) => c.uid === categoryUid);
            if (!target)
                return;
            const byUid = new Map(target.items.map((i) => [i.uid, i]));
            const reordered = [];
            for (const uid of itemUids) {
                const it = byUid.get(uid);
                if (it)
                    reordered.push(it);
            }
            for (const it of target.items) {
                if (!itemUids.includes(it.uid))
                    reordered.push(it);
            }
            target.items = reordered;
        },
        setCategorySortingDirty(state, action) {
            state.categorySortingDirty = action.payload;
        },
        setItemSortingDirty(state, action) {
            const { categoryUid, dirty } = action.payload;
            const idx = state.itemSortingDirtyCategoryIds.indexOf(categoryUid);
            if (dirty && idx === -1) {
                state.itemSortingDirtyCategoryIds.push(categoryUid);
            }
            else if (!dirty && idx !== -1) {
                state.itemSortingDirtyCategoryIds.splice(idx, 1);
            }
        },
    },
    selectors: {
        selectAdminFaqCategories: (state) => state.categories,
        selectAdminFaqLoadStatus: (state) => state.loadStatus,
        selectCategorySortingDirty: (state) => state.categorySortingDirty,
        // WR-05: consumers should select the dirty-ids array directly and call
        // `.includes(uid)` locally. The previous curried-selector pattern
        // (`selectIsItemSortingDirtyFor`) returned a fresh arrow function on
        // every invocation, defeating useSelector's `===` equality check and
        // causing every consumer to re-render on ANY adminFaq slice change.
        selectItemSortingDirtyCategoryIds: (state) => state.itemSortingDirtyCategoryIds,
    },
});
export const { setAdminFaqCategories, setLoadStatus, upsertCategory, removeCategory, upsertItem, removeItem, setCategorySorting, setItemSorting, setCategorySortingDirty, setItemSortingDirty, } = adminFaqSlice.actions;
export const { selectAdminFaqCategories, selectAdminFaqLoadStatus, selectCategorySortingDirty, selectItemSortingDirtyCategoryIds, } = adminFaqSlice.selectors;
export default adminFaqSlice.reducer;
